if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      htmlCacheForRequestUrl = new Map();
      batchRequestCount = 5
      htmlCacheDuration = 5*60*1000
      htmlCacheRefreshLoopTimer = null
      abortHtmlCacheBatchRequest = undefined
      isHtmlCacheBatchRequestAborted = false

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get mediaGallery() {
        return this.querySelector("media-gallery");
      }

      get productId() {
        return this.getAttribute("data-product-id");
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }

      get isMobile() {
        return Boolean(FoxTheme.config?.mqlMobile)
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
        requestAnimationFrame(() => {
          this.initHtmlCache()
          this.getHtmlCacheForOptions().finally(() => {
            this.startRefreshHtmlCacheLoop()
          })
        })
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      getRequestOptionUrl(value) {
        let url = this.dataset.url ?? ''
        url += `?section_id=${this.sectionId}&option_values=${value}`
        return url
      }
      
      initHtmlCache() {
        const parentElement = this.parentElement
        if (!parentElement) return
        const selectedOptionValues = this.variantSelectors.selectedOptionValues ?? []
        const url = this.getRequestOptionUrl(selectedOptionValues?.join(','))
        this.htmlCacheForRequestUrl.set(url, {
          data: parentElement.outerHTML,
          cacheTime: Date.now()
        })
      }

      async getHtmlCacheForOptions() {
        const optionValues = this.variantSelectors.optionValues ?? []
        if (optionValues?.length) {
          let index = 0;
          const len = Math.ceil(optionValues.length / this.batchRequestCount)
          this.abortHtmlCacheBatchRequest?.abort()
          this.abortHtmlCacheBatchRequest = new AbortController();
          while (index < len) {
            const requestOptions = optionValues.slice(index * this.batchRequestCount, (index + 1)*this.batchRequestCount)
            const promises = []
            requestOptions.forEach((value) => {
              const requestUrl = this.getRequestOptionUrl(value)
              const cacheData = this.htmlCacheForRequestUrl.get(requestUrl)
              if (!cacheData || Date.now() - cacheData.cacheTime > this.htmlCacheDuration) {
                promises.push(
                  fetch(requestUrl, { signal: this.abortHtmlCacheBatchRequest.signal })
                  .then((response) => response.text())
                  .then((responseText) => {
                    this.htmlCacheForRequestUrl.set(requestUrl, {
                      data: responseText,
                      cacheTime: Date.now()
                    })

                    return responseText
                  }).catch((error) =>{
                    if (error.name === "AbortError") {
                      console.log("Fetch aborted by user");
                      this.isHtmlCacheBatchRequestAborted = true
                    } else {
                      console.error(error);
                    }
                  })
                )
              }
            })
            await Promise.allSettled(promises)
            index++
          }
        }
      }

      startRefreshHtmlCacheLoop() {
        if (this.htmlCacheRefreshLoopTimer) {
          clearTimeout(this.htmlCacheRefreshLoopTimer)
        }
        this.htmlCacheRefreshLoopTimer = setTimeout(async() => {
          try {
            await this.getHtmlCacheForOptions()
          } finally {
            this.startRefreshHtmlCacheLoop()
          }
        }, 60*1000);
      }

      stopRefreshHtmlCacheLoop() {
        if (this.htmlCacheRefreshLoopTimer) {
          clearTimeout(this.htmlCacheRefreshLoopTimer)
        }
        this.abortHtmlCacheBatchRequest?.abort()
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;
        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || "main-product";
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById("shopify-section-sticky-atc-bar"),
              html.getElementById("shopify-section-sticky-atc-bar"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          const isQuickViewMode = viewMode === "quick-view"
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const quickViewContentDom = this.findParent(this, 'quick-view__content')

          const variant = this.getSelectedVariant(html);
          const oldScrollTop = window.scrollY, oldScrollTopForModel = quickViewContentDom ? quickViewContentDom.scrollY : null
          this.updateMediaGallery(html);          
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (isQuickViewMode) {
             requestAnimationFrame(() => {
              quickViewContentDom.scrollTo({ top: 0 })            
            })
          } else if (this.isMobile) {
            requestAnimationFrame(() => {
              window.scrollTo({ top: 0 })            
            })
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant, true);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Badges", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("PricePerItem", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("Volume");

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(
            `#QuantityRules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#VolumeNote-${this.dataset.section}`
          )?.classList.remove("hidden");

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`
          );

          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true,
            FoxTheme.variantStrings.soldOut
          );

          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            }
          );

          document.dispatchEvent(
            new CustomEvent("variant:changed", {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      findParent(parentElement, className) {
        if (!parentElement || !className) return null
        if (parentElement.classList.contains(className)) return parentElement
        return this.findParent(parentElement.parentElement, className)
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getHtmlByRequestUrlWithCache(requestUrl) {
        if (!requestUrl) {
          return Promise.reject('Empty requestUrl');
        }
        const cacheData = this.htmlCacheForRequestUrl.get(requestUrl)
        if (cacheData) {
          const cacheTime = cacheData.cacheTime
          if (cacheTime && Date.now() - cacheTime <= this.htmlCacheDuration) {
            return Promise.resolve(cacheData.data)
          }
        }
        return fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.htmlCacheForRequestUrl.set(requestUrl, {
              data: responseText,
              cacheTime: Date.now()
            })

            return responseText
          })
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.stopRefreshHtmlCacheLoop()
        this.getHtmlByRequestUrlWithCache(requestUrl)
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          }).finally(() => {
            if (!this.isHtmlCacheBatchRequestAborted) {
              this.startRefreshHtmlCacheLoop()
            } else {
              this.getHtmlCacheForOptions().finally(() => {
                this.startRefreshHtmlCacheLoop()
              })
              this.isHtmlCacheBatchRequestAborted = false
            }
          });
      }

      updateMediaGallery(html) {
        const mediaGallery = html.querySelector("media-gallery");
        if (mediaGallery) {
          HTMLUpdateUtility.viewTransition(
            this.mediaGallery,
            mediaGallery,
            this.preProcessHtmlCallbacks
          );
        }
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? "";
            input.dispatchEvent(new Event("change", { bubbles: true }));
          });
      }

      updateMedia(variant, inited) {
        const productMedia = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === "function") {
            productMedia.init();
            if (inited) {
              productMedia.setActiveMedia(variant);
            }
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(
          `ProductShare-${this.dataset.section}`
        );
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${variantId}`
        );
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
          `product-form-${this.dataset.section}`
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.textContent = this.decoded(
            FoxTheme.variantStrings.addToCart
          );
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(
          `Inventory-${this.dataset.section}`
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add("hidden");
        if (inventory) inventory.classList.add("hidden");
        if (sku) sku.classList.add("hidden");
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute("data-original-section")) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
          {
            data: {
              sectionId: this.sectionId,
              productId: this.productId,
            },
          }
        );
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart")?.classList.add(
          "btn--loading"
        );

        fetch(
          `${this.getAttribute(
            "data-url"
          )}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(
              this.sectionId,
              this.productId,
              parsedHTML
            );
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector(".quantity__rules-cart")?.classList.remove(
              "btn--loading"
            );
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
